import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { useForm, useAuthStore } from "hooks";
import Swal from "sweetalert2";

export interface ResetPasswordProps {
  className?: string;
}

const loginFormFields = {
  newPassword: "",
  confirmPassword: "",
};

const inputDark =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-slate-500 transition focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30";
const labelDark = "mb-1.5 block text-sm font-medium text-slate-300";
const btnGradient =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-7 py-3 text-base font-bold uppercase tracking-wide text-white shadow-lg shadow-violet-600/30 transition-all duration-200 hover:from-violet-500 hover:to-fuchsia-500 hover:shadow-violet-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 active:scale-95 disabled:opacity-70";

const PageResetPassword: FC<ResetPasswordProps> = ({ className = "" }) => {
  const { resetPassword, errorMessage } = useAuthStore();
  const [message, setMessage] = useState("");
  const [responseState, setResponse] = useState({ ok: false, message: "" });
  const { token } = useParams();
  const { newPassword, confirmPassword, onInputChange } = useForm(loginFormFields);
  const [hiddenForm, setHiddenForm] = useState(true);

  useEffect(() => {
    if (errorMessage !== undefined) {
      Swal.fire("Error en la autenticación", errorMessage, "error");
    }
  }, [errorMessage]);

  const handleReset = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    if (newPassword.length < 6 || confirmPassword.length < 6) {
      setMessage("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("Las contraseñas no coinciden");
      return;
    }
    try {
      resetPassword({ password: newPassword, token }).then((response: any) => {
        setResponse(response);
        if (response.ok) {
          setMessage("Se ha realizado correctamente el cambio de contraseña.");
          setHiddenForm(false);
        } else {
          setMessage("Ha pasado mucho tiempo, debes solicitar un nuevo password.");
          setHiddenForm(false);
        }
      });
    } catch (error) {
      console.log("Error al cambiar la contraseña", error);
    }
  };

  return (
    <div className={`nc-PageResetPassword relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0c0a16] to-[#15101f] px-4 py-16 ${className}`} data-nc-id="PageResetPassword">
      <Helmet>
        <title>Restablecer contraseña · Karaoke Room Barcelona</title>
      </Helmet>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-violet-600/20 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-md">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-3xl font-bold uppercase tracking-wide text-white sm:text-4xl">Restablecer contraseña</h1>
          <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
        </div>

        <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur-xl sm:p-8">
          {hiddenForm && (
            <form className="grid grid-cols-1 gap-5" onSubmit={handleReset}>
              <div>
                <label htmlFor="newPassword" className={labelDark}>Nueva contraseña</label>
                <input id="newPassword" type="password" placeholder="••••••••" name="newPassword" value={newPassword} onChange={onInputChange} className={inputDark} />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelDark}>Confirmar contraseña</label>
                <input id="confirmPassword" type="password" placeholder="••••••••" name="confirmPassword" value={confirmPassword} onChange={onInputChange} className={inputDark} />
              </div>
              <button type="submit" className={btnGradient}>Restablecer contraseña</button>
            </form>
          )}

          {message && (
            <p className={`${hiddenForm ? "mt-5" : ""} rounded-lg border px-4 py-3 text-center text-sm ${responseState.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-rose-500/30 bg-rose-500/10 text-rose-300"}`}>
              {message}
            </p>
          )}

          <p className="mt-6 text-center text-sm text-slate-400">
            {responseState.ok ? (
              <Link to="/login" className="font-semibold text-violet-300 hover:text-violet-200">Iniciar sesión</Link>
            ) : (
              <Link to="/forgot-password" className="font-semibold text-violet-300 hover:text-violet-200">Solicitar un nuevo enlace</Link>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PageResetPassword;
